import { Box } from '@veracity/vui'
import styled from 'styled-components'

import {
  IsoBa,
  ScenarioCompareDTO,
  ScenariosComparisonGroupedAnnuallyDTO
} from '@/interfaces'
import { formatters, isPvScenario, isWindScenario } from '@/utils'

import { ScenarioComparisonBooleanIcon } from './ScenarioComparisonBooleanIcon'
import { ScenarioComparisonPreviewCard } from './ScenarioComparisonPreviewCard'
import { ScenarioComparisonPreviewCardContainer } from './ScenarioComparisonPreviewCardContainer'

const FullScreenPrintableContainer = styled(Box)`
  @media print {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    padding: 5px;
    margin-left: -5px;
    background: white;
  }
`

interface Props {
  areAllRowsExpanded: boolean
  comparisonInfo: ScenarioCompareDTO[]
  scenarioComparisonGroupedAnnually: ScenariosComparisonGroupedAnnuallyDTO[]
}

export const ScenarioComparisonPreview = ({
  areAllRowsExpanded,
  comparisonInfo,
  scenarioComparisonGroupedAnnually
}: Props) => {
  const hasPvData = comparisonInfo.some(x =>
    isPvScenario(x.systemConfiguration)
  )

  const hasWindData = comparisonInfo.some(x =>
    isWindScenario(x.systemConfiguration)
  )

  const isSpainComparison = comparisonInfo.some(
    x => x.isoCode === IsoBa.ES_PENINSULA
  )

  const currency = isSpainComparison ? 'EUR' : 'USD'

  return (
    <FullScreenPrintableContainer column gap={1}>
      <ScenarioComparisonPreviewCard
        areAllRowsExpanded={areAllRowsExpanded}
        items={[
          {
            label: 'Scenario',
            items: comparisonInfo.map(x => x.scenarioName),
            isBoldedRow: true
          },
          { label: 'Project', items: comparisonInfo.map(x => x.projectName) }
        ]}
      />

      <ScenarioComparisonPreviewCard
        title="Project Summary"
        areAllRowsExpanded={areAllRowsExpanded}
        items={[
          {
            label: 'System Configuration',
            items: comparisonInfo.map(x => x.systemConfigurationString),
            isCollapsableRow: true
          },
          {
            label: 'Term',
            items: comparisonInfo.map(x => x.term && `${x.term} year(s)`),
            isCollapsableRow: true
          },
          {
            label: 'IRR',
            items: comparisonInfo.map(
              x => x.irr && formatters.percentage(x.irr)
            ),
            isCollapsableRow: true
          },
          {
            label: 'Present Value of Total Expenses',
            items: comparisonInfo.map(
              x =>
                x.totalExpenses &&
                formatters.currency(x.totalExpenses, currency)
            ),
            isCollapsableRow: true
          },
          {
            label: 'Present Value of Total Earnings',
            items: comparisonInfo.map(
              x =>
                x.totalEarnings &&
                formatters.currency(x.totalEarnings, currency)
            ),
            isCollapsableRow: true
          },
          {
            label: 'Present Value of Total Profit',
            items: comparisonInfo.map(
              x => x.totalProfit && formatters.currency(x.totalProfit, currency)
            ),
            isCollapsableRow: true
          },
          {
            label: 'ES Average Cycles',
            items: comparisonInfo.map(
              x => x.esAverageCycles && formatters.number(x.esAverageCycles)
            ),
            isCollapsableRow: true
          },
          {
            label: 'ES Average SOC',
            items: comparisonInfo.map(
              x => x.esAverageSOC && formatters.percentage(x.esAverageSOC)
            ),
            isCollapsableRow: true
          },
          {
            label: 'Interconnection Limit',
            items: comparisonInfo.map(
              x =>
                x.interconnectionLimit &&
                `${formatters.number(x.interconnectionLimit) || ''} MW`
            ),
            isCollapsableRow: true
          },
          {
            label: 'Run Options',
            items: comparisonInfo.map(x => x.yearsToRunString),
            isCollapsableRow: true
          }
        ]}
      />

      <ScenarioComparisonPreviewCard
        title="Input Dataset Name"
        areAllRowsExpanded={areAllRowsExpanded}
        items={[
          {
            label: 'Wholesale Market Price',
            items: comparisonInfo.map(x => x.wholesaleMarketDatasetName),
            isCollapsableRow: true
          },
          {
            label: 'Finances',
            items: comparisonInfo.map(x => x.financesDatasetName),
            isCollapsableRow: true
          },
          {
            label: 'Income Adjustment',
            items: comparisonInfo.map(x => x.incomeAdjustmentDatasetName),
            isCollapsableRow: true
          },
          {
            label: 'Loss Factors',
            items: comparisonInfo.map(x => x.lossFactorsDatasetName),
            isCollapsableRow: true
          },
          {
            label: 'Production Profiles',
            items: comparisonInfo.map(x => x.productionProfileDatasetName),
            isCollapsableRow: true
          },
          {
            label: 'Other BESS Inputs',
            items: comparisonInfo.map(x => x.otherBessInputDatasetName),
            isCollapsableRow: true
          }
        ]}
      />

      <ScenarioComparisonPreviewCard
        title="ES"
        areAllRowsExpanded={areAllRowsExpanded}
        items={[
          {
            label: 'Power Capacity',
            items: comparisonInfo.map(
              x =>
                x.esPowerCapacity &&
                `${formatters.number(x.esPowerCapacity) || ''} MW`
            )
          },
          {
            label: 'Energy Capacity',
            items: comparisonInfo.map(
              x =>
                x.esEnergyCapacity &&
                `${formatters.number(x.esEnergyCapacity) || ''} MWh`
            )
          },
          {
            label: 'Maximum Discharge Power',
            items: comparisonInfo.map(
              x =>
                x.maximumDischargePower &&
                `${formatters.number(x.maximumDischargePower) || ''} MW`
            ),
            isCollapsableRow: true
          }
        ]}
      />

      {hasPvData && (
        <ScenarioComparisonPreviewCard
          title="PV"
          areAllRowsExpanded={areAllRowsExpanded}
          items={[
            {
              label: 'DC Capacity',
              items: comparisonInfo.map(
                x =>
                  x.pvDcCapacity &&
                  `${formatters.number(x.pvDcCapacity) || ''} MW`
              )
            },
            {
              label: 'AC Capacity',
              items: comparisonInfo.map(
                x =>
                  x.pvAcCapacity &&
                  `${formatters.number(x.pvAcCapacity) || ''} MW`
              )
            },
            {
              label: 'DC-AC Inverter Efficiency',
              items: comparisonInfo.map(
                x =>
                  x.maximumDischargePower &&
                  formatters.percentage(x.maximumDischargePower)
              ),
              isCollapsableRow: true
            },
            {
              label: 'Annual Derating',
              items: comparisonInfo.map(
                x =>
                  x.pvAnnualDerating &&
                  formatters.percentage(x.pvAnnualDerating)
              ),
              isCollapsableRow: true
            }
          ]}
        />
      )}

      {hasWindData && (
        <ScenarioComparisonPreviewCard
          title="Wind"
          areAllRowsExpanded={areAllRowsExpanded}
          items={[
            {
              label: 'AC Capacity',
              items: comparisonInfo.map(
                x =>
                  x.windAcCapacity &&
                  `${formatters.number(x.windAcCapacity) || ''} MW`
              )
            },
            {
              label: 'Total Efficiency',
              items: comparisonInfo.map(
                x =>
                  x.windTotalEfficiency &&
                  formatters.percentage(x.windTotalEfficiency)
              ),
              isCollapsableRow: true
            }
          ]}
        />
      )}

      <ScenarioComparisonPreviewCard
        title="Market & Finance"
        areAllRowsExpanded={areAllRowsExpanded}
        items={[
          {
            label: 'Real-Time Energy Participation Limit',
            items: comparisonInfo.map(x => (
              <ScenarioComparisonBooleanIcon
                key={x.scenarioId}
                value={x.realTimeEnergyParticipationLimit}
              />
            )),
            isCollapsableRow: true
          },
          {
            label: 'Regulation Participation Limit',
            items: comparisonInfo.map(x => (
              <ScenarioComparisonBooleanIcon
                key={x.scenarioId}
                value={x.regulationParticipationLimit}
              />
            )),
            isCollapsableRow: true
          },
          {
            label: 'Spinning Reserve Participation Limit',
            items: comparisonInfo.map(x => (
              <ScenarioComparisonBooleanIcon
                key={x.scenarioId}
                value={x.spinningReserveParticipationLimit}
              />
            )),
            isCollapsableRow: true
          }
        ]}
      />

      <ScenarioComparisonPreviewCardContainer
        title="Cashflow"
        items={scenarioComparisonGroupedAnnually.map(yearlyData => ({
          title: `${yearlyData.year}`,
          areAllRowsExpanded,
          items: [
            {
              label: 'ES Operating Income',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  !!cashflow &&
                  formatters.currency(cashflow['ES Operating'], currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'PV Operating Income',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  !!cashflow &&
                  formatters.currency(cashflow['PV Operating'], currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'Capacity Market Income',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  !!cashflow &&
                  formatters.currency(cashflow['Capacity Market'], currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'Operating Expenditure',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  !!cashflow &&
                  formatters.currency(
                    cashflow['Operating Expenditure'],
                    currency
                  )
              ),
              isCollapsableRow: true
            },
            {
              label: 'Equity Payment',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  !!cashflow &&
                  formatters.currency(cashflow['Equity Payment'], currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'Federal ITC Rebate',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  !!cashflow &&
                  formatters.currency(cashflow['Federal ITC Rebate'], currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'Tax Depreciation',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  !!cashflow &&
                  formatters.currency(cashflow['Tax Depreciation'], currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'Pre-Tax Cashflow',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  !!cashflow &&
                  formatters.currency(cashflow['ES Pre-Tax Cashflow'], currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'Cumulative Cashflow',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  !!cashflow &&
                  formatters.currency(
                    cashflow['ES Cumulative Cashflow'],
                    currency
                  )
              ),
              isCollapsableRow: true
            },
            {
              label: 'ES Average Cycles',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  cashflow?.['ES Average Cycles'] &&
                  formatters.number(cashflow['ES Average Cycles'])
              ),
              isCollapsableRow: true
            },
            {
              label: 'ES Average SOC',
              items: yearlyData.cashflow?.map(
                cashflow =>
                  cashflow?.['ES Average SOC'] &&
                  formatters.percentage(cashflow['ES Average SOC'])
              ),
              isCollapsableRow: true
            }
          ]
        }))}
      />

      <ScenarioComparisonPreviewCardContainer
        title="ES Operating Revenue"
        items={scenarioComparisonGroupedAnnually.map(yearlyData => ({
          title: `${yearlyData.year}`,
          areAllRowsExpanded,
          items: yearlyData.esOperatingSpainRevenue
            ? [
                {
                  label: 'DAE',
                  items: yearlyData.esOperatingSpainRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(operatingRevenue.DAE, currency)
                  ),
                  isCollapsableRow: true
                },
                {
                  label: 'mFRRU',
                  items: yearlyData.esOperatingSpainRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(operatingRevenue.mFRRU, currency)
                  ),
                  isCollapsableRow: true
                },
                {
                  label: 'aFRRU',
                  items: yearlyData.esOperatingSpainRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(operatingRevenue.aFRRU, currency)
                  ),
                  isCollapsableRow: true
                },
                {
                  label: 'aFRRD',
                  items: yearlyData.esOperatingSpainRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(operatingRevenue.aFRRD, currency)
                  ),
                  isCollapsableRow: true
                },
                {
                  label: 'mFRRD',
                  items: yearlyData.esOperatingSpainRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(operatingRevenue.mFRRD, currency)
                  ),
                  isCollapsableRow: true
                },
                {
                  label: 'Total',
                  items: yearlyData.esOperatingSpainRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(operatingRevenue.Total, currency)
                  )
                }
              ]
            : [
                {
                  label: 'DAE',
                  items: yearlyData.esOperatingCanUsRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(operatingRevenue.DAE, currency)
                  ),
                  isCollapsableRow: true
                },
                {
                  label: 'RTE',
                  items: yearlyData.esOperatingCanUsRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(operatingRevenue.RTE, currency)
                  ),
                  isCollapsableRow: true
                },
                {
                  label: 'Regulation Up',
                  items: yearlyData.esOperatingCanUsRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(
                        operatingRevenue['Regulation Up'],
                        currency
                      )
                  ),
                  isCollapsableRow: true
                },
                {
                  label: 'Regulation Down',
                  items: yearlyData.esOperatingCanUsRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(
                        operatingRevenue['Regulation Down'],
                        currency
                      )
                  ),
                  isCollapsableRow: true
                },
                {
                  label: 'Spinning Reserve',
                  items: yearlyData.esOperatingCanUsRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(
                        operatingRevenue['Spinning Reserve'],
                        currency
                      )
                  ),
                  isCollapsableRow: true
                },
                {
                  label: 'Total',
                  items: yearlyData.esOperatingCanUsRevenue?.map(
                    operatingRevenue =>
                      !!operatingRevenue &&
                      formatters.currency(operatingRevenue.Total, currency)
                  )
                }
              ]
        }))}
      />

      <ScenarioComparisonPreviewCardContainer
        title="ES Operating Cost"
        items={scenarioComparisonGroupedAnnually.map(yearlyData => ({
          title: `${yearlyData.year}`,
          areAllRowsExpanded,
          items: [
            {
              label: 'DAE ',
              items: yearlyData.esOperatingCost?.map(
                operatingCost =>
                  !!operatingCost &&
                  formatters.currency(operatingCost.DAE, currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'RTE ',
              items: yearlyData.esOperatingCost?.map(
                operatingCost =>
                  !!operatingCost &&
                  formatters.currency(operatingCost.RTE, currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'PV ',
              items: yearlyData.esOperatingCost?.map(
                operatingCost =>
                  !!operatingCost &&
                  formatters.currency(operatingCost.PV, currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'Wind  ',
              items: yearlyData.esOperatingCost?.map(
                operatingCost =>
                  !!operatingCost &&
                  formatters.currency(operatingCost.Wind, currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'Total',
              items: yearlyData.esOperatingCost?.map(
                operatingCost =>
                  !!operatingCost &&
                  formatters.currency(operatingCost.Total, currency)
              )
            }
          ]
        }))}
      />

      <ScenarioComparisonPreviewCardContainer
        title="Resource Income"
        items={scenarioComparisonGroupedAnnually.map(yearlyData => ({
          title: `${yearlyData.year}`,
          areAllRowsExpanded,
          items: [
            {
              label: 'ES Operating Income ',
              items: yearlyData.resourceIncome?.map(
                resourceIncome =>
                  !!resourceIncome &&
                  formatters.currency(
                    resourceIncome['ES Operating Income'],
                    currency
                  )
              ),
              isCollapsableRow: true
            },
            {
              label: 'PV Operating Income',
              items: yearlyData.resourceIncome?.map(
                resourceIncome =>
                  !!resourceIncome &&
                  formatters.currency(
                    resourceIncome['PV Operating Income'],
                    currency
                  )
              ),
              isCollapsableRow: true
            },
            {
              label: 'Wind Operating Income',
              items: yearlyData.resourceIncome?.map(
                resourceIncome =>
                  !!resourceIncome &&
                  formatters.currency(
                    resourceIncome['Wind Operating Income'],
                    currency
                  )
              ),
              isCollapsableRow: true
            },

            {
              label: 'Total',
              items: yearlyData.resourceIncome?.map(
                resourceIncome =>
                  !!resourceIncome &&
                  formatters.currency(
                    resourceIncome['ES Operating Income'] +
                      resourceIncome['PV Operating Income'] +
                      resourceIncome['Wind Operating Income'],
                    currency
                  ) // TODO: Add Total at backend
              )
            }
          ]
        }))}
      />

      <ScenarioComparisonPreviewCard
        title="Project Cost"
        areAllRowsExpanded={areAllRowsExpanded}
        items={[
          {
            label: 'Total PV Cost',
            items: comparisonInfo?.map(
              x =>
                x.projectCost &&
                formatters.currency(x.projectCost['Total PV Cost'], currency)
            ),
            isCollapsableRow: true
          },
          {
            label: 'Total ES Cost',
            items: comparisonInfo?.map(
              x =>
                x.projectCost &&
                formatters.currency(x.projectCost['Total ES Cost'], currency)
            ),
            isCollapsableRow: true
          },
          {
            label: 'Total Development Cost',
            items: comparisonInfo?.map(
              x =>
                x.projectCost &&
                formatters.currency(
                  x.projectCost['Total Development Cost'],
                  currency
                )
            ),
            isCollapsableRow: true
          },
          {
            label: 'Total Wind Cost',
            items: comparisonInfo?.map(
              x =>
                x.projectCost &&
                formatters.currency(x.projectCost['Total Wind Cost'], currency)
            ),
            isCollapsableRow: true
          },
          {
            label: 'Total Equipment Sales Tax',
            items: comparisonInfo?.map(
              x =>
                x.projectCost &&
                formatters.currency(
                  x.projectCost['Total Equip Sales Tax'],
                  currency
                )
            ),
            isCollapsableRow: true
          },
          {
            label: 'Total',
            items: comparisonInfo?.map(x =>
              formatters.currency(x.projectCostTotal, currency)
            )
          }
        ]}
      />

      <ScenarioComparisonPreviewCard
        title="ES Cost"
        areAllRowsExpanded={areAllRowsExpanded}
        items={[
          {
            label: 'DC Side',
            items: comparisonInfo?.map(
              x =>
                x.esCost?.['DC Side'] &&
                formatters.currency(x.esCost['DC Side'], currency)
            ),
            isCollapsableRow: true
          },
          {
            label: 'Power Conversion System',
            items: comparisonInfo?.map(
              x =>
                x.esCost?.['Power Conversion System'] &&
                formatters.currency(
                  x.esCost['Power Conversion System'],
                  currency
                )
            ),
            isCollapsableRow: true
          },
          {
            label: 'Grid Management System',
            items: comparisonInfo?.map(
              x =>
                x.esCost?.['Grid Management System'] &&
                formatters.currency(
                  x.esCost['Grid Management System'],
                  currency
                )
            ),
            isCollapsableRow: true
          },
          {
            label: 'LV Xmer and Switchgear',
            items: comparisonInfo?.map(
              x =>
                x.esCost?.['LV Xmer and Switchgear'] &&
                formatters.currency(
                  x.esCost['LV Xmer and Switchgear'],
                  currency
                )
            ),
            isCollapsableRow: true
          },
          {
            label: 'Total',
            items: comparisonInfo?.map(x =>
              formatters.currency(x.esCostTotal, currency)
            )
          }
        ]}
      />

      <ScenarioComparisonPreviewCard
        title="Development Cost"
        areAllRowsExpanded={areAllRowsExpanded}
        items={[
          {
            label: 'Site Infrastructure',
            items: comparisonInfo?.map(
              x =>
                x.developmentCost?.['Site Infrastructure'] &&
                formatters.currency(
                  x.developmentCost['Site Infrastructure'],
                  currency
                )
            ),
            isCollapsableRow: true
          },
          {
            label: 'Interconnection and Network Upgrade',
            items: comparisonInfo?.map(
              x =>
                x.developmentCost?.['Interconnection and Network Upgrade'] &&
                formatters.currency(
                  x.developmentCost?.['Interconnection and Network Upgrade'],
                  currency
                )
            ),
            isCollapsableRow: true
          },
          {
            label: 'Owner Cost',
            items: comparisonInfo?.map(
              x =>
                x.developmentCost?.['Owner Cost'] &&
                formatters.currency(x.developmentCost['Owner Cost'], currency)
            ),
            isCollapsableRow: true
          },
          {
            label: 'Total',
            items: comparisonInfo?.map(x =>
              formatters.currency(x.developmentCostTotal, currency)
            )
          }
        ]}
      />

      {hasPvData && (
        <ScenarioComparisonPreviewCard
          title="PV Cost"
          areAllRowsExpanded={areAllRowsExpanded}
          items={[
            {
              label: 'Module Equipment',
              items: comparisonInfo?.map(
                x =>
                  x.pvCost &&
                  formatters.currency(x.pvCost['Module Equipment'], currency)
              ),
              isCollapsableRow: true
            },
            {
              label: 'Inverter and Control',
              items: comparisonInfo?.map(
                x =>
                  x.pvCost &&
                  formatters.currency(
                    x.pvCost['Inverter and Control'],
                    currency
                  )
              ),
              isCollapsableRow: true
            },
            {
              label: 'Engineering and Construction',
              items: comparisonInfo?.map(
                x =>
                  x.pvCost &&
                  formatters.currency(
                    x.pvCost['Engg and Construction'],
                    currency
                  )
              ),
              isCollapsableRow: true
            },
            {
              label: 'LV Xmer and Switchgear',
              items: comparisonInfo?.map(
                x =>
                  x.pvCost &&
                  formatters.currency(
                    x.pvCost['LV Xmer and Switchgear'],
                    currency
                  )
              ),
              isCollapsableRow: true
            },
            {
              label: 'Total',
              items: comparisonInfo?.map(x =>
                formatters.currency(x.pvCostTotal, currency)
              )
            }
          ]}
        />
      )}
    </FullScreenPrintableContainer>
  )
}
